/**
 * Cross-app origin helpers for the Lion Pro multi-app setup.
 *
 * - pro-web:       :5173 / pro(-dev).lionenergy.com / pro.localhost
 * - account-web:   :5174 / account(-dev).lionenergy.com / account.localhost  (auth hub)
 * - certification: :5181 / certification(-dev).lionenergy.com / certification.localhost
 *
 * Prod preview (pre-cutover): the new certification SPA is reachable at
 * certification-legacy.lionenergy.com while certification.lionenergy.com still
 * serves the legacy portal. After flip, change {@link PROD_CERTIFICATION_APP_HOST}
 * to `certification.lionenergy.com`.
 *
 * Never derive sibling hosts with a naive `certification` → `account` string
 * replace — that turns `certification-legacy` into the non-existent
 * `account-legacy`.
 */

/** Prod hostname where the new certification SPA is publicly reachable. */
export const PROD_CERTIFICATION_APP_HOST = "certification-legacy.lionenergy.com";

const PROD_ACCOUNT_ORIGIN = "https://account.lionenergy.com";
const PROD_PRO_ORIGIN = "https://pro.lionenergy.com";
const DEV_ACCOUNT_ORIGIN = "https://account-dev.lionenergy.com";
const DEV_PRO_ORIGIN = "https://pro-dev.lionenergy.com";
const DEV_CERT_ORIGIN = "https://certification-dev.lionenergy.com";

function currentHostname(): string {
  return typeof window !== "undefined" ? window.location.hostname : "";
}

function currentProtocol(): string {
  return typeof window !== "undefined" ? window.location.protocol : "https:";
}

function currentPort(): string {
  return typeof window !== "undefined" ? window.location.port : "";
}

/** Plain loopback (port-based local apps). */
function isLoopbackHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Subdomain-style local hosts (pro.localhost, account.localhost, …).
 * These are convenient for cookie Domain=.localhost sharing, but must never
 * fall through to a portless origin — browsers treat that as :80/:443.
 */
function isLocalSubdomainHost(hostname: string): boolean {
  return /^(pro|account|certification)\.localhost$/.test(hostname);
}

function isLocalDevHost(hostname: string): boolean {
  return isLoopbackHost(hostname) || isLocalSubdomainHost(hostname);
}

function isProdLionEnergyHost(hostname: string): boolean {
  return (
    hostname === "pro.lionenergy.com" ||
    hostname === "account.lionenergy.com" ||
    hostname === "certification.lionenergy.com" ||
    hostname === "certification-legacy.lionenergy.com" ||
    hostname === "cert.lionenergy.com"
  );
}

function isDevLionEnergyHost(hostname: string): boolean {
  return (
    hostname === "pro-dev.lionenergy.com" ||
    hostname === "account-dev.lionenergy.com" ||
    hostname === "certification-dev.lionenergy.com"
  );
}

/**
 * Playwright E2E System page: local (UI + fixture) and pro-dev (live CI summaries).
 * Not available on production — we do not run E2E against prod yet.
 */
export function isE2eTestingAvailable(
  hostname: string = currentHostname(),
): boolean {
  if (isLocalDevHost(hostname)) return true;
  return hostname === "pro-dev.lionenergy.com";
}

/**
 * Swap only `pro` / `account` / `certification` (optional `-dev`) labels.
 * Does not match `certification-legacy` (would become `account-legacy`).
 */
function swapAppLabel(
  hostname: string,
  to: "pro" | "account" | "certification",
): string | null {
  const match = hostname.match(/^(pro|account|certification)(-dev)?\.(.+)$/);
  if (!match) return null;
  const envSuffix = match[2] ?? "";
  return `${to}${envSuffix}.${match[3]}`;
}

/** True when hostname should boot the certification SPA (CloudFront / local). */
export function isCertificationAppHost(hostname: string = currentHostname()): boolean {
  if (import.meta.env.VITE_APP_TARGET === "certification") return true;
  if (hostname === "certification.localhost") return true;
  if (
    typeof window !== "undefined" &&
    isLoopbackHost(hostname) &&
    window.location.port === "5181"
  ) {
    return true;
  }
  // Primary + preview hosts (whichever is pointed at CloudFront).
  return (
    hostname === "certification.lionenergy.com" ||
    hostname === "certification-legacy.lionenergy.com" ||
    hostname === "certification-dev.lionenergy.com" ||
    hostname === "cert.lionenergy.com"
  );
}

/** True when the current page is the account (auth) app. */
export function isAccountAppHost(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname, port } = window.location;
  if (import.meta.env.VITE_APP_TARGET === "account") return true;
  if (/^account(-dev)?\.lionenergy\.com$/.test(hostname)) return true;
  if (hostname === "account.localhost") return true;
  // Local account-web runs on :5174 (plain localhost / 127.0.0.1)
  if (isLoopbackHost(hostname) && port === "5174") {
    return true;
  }
  return false;
}

/** Origin of the shared account (auth) app. */
export function getAccountAppOrigin(): string {
  const hostname = currentHostname();
  const protocol = currentProtocol();

  if (isLoopbackHost(hostname)) {
    return `${protocol}//${hostname}:5174`;
  }
  if (isLocalSubdomainHost(hostname)) {
    // Google OAuth only allows http://localhost (not *.localhost). Keep the
    // auth hub on localhost:5174 so the callback, sessionStorage, and
    // host-only auth cookie all stay on the same host.
    return `${protocol}//localhost:5174`;
  }
  if (isProdLionEnergyHost(hostname)) {
    return PROD_ACCOUNT_ORIGIN;
  }
  if (isDevLionEnergyHost(hostname)) {
    return DEV_ACCOUNT_ORIGIN;
  }
  const swapped = swapAppLabel(hostname, "account");
  if (swapped) {
    return `${protocol}//${swapped}`;
  }
  const port = currentPort();
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
}

/** Origin of the certification app (new SPA — preview host until cutover). */
export function getCertificationAppOrigin(): string {
  const hostname = currentHostname();
  const protocol = currentProtocol();

  if (isLoopbackHost(hostname)) {
    return `${protocol}//${hostname}:5181`;
  }
  if (isLocalSubdomainHost(hostname)) {
    return `${protocol}//localhost:5181`;
  }
  if (isProdLionEnergyHost(hostname)) {
    return `https://${PROD_CERTIFICATION_APP_HOST}`;
  }
  if (isDevLionEnergyHost(hostname)) {
    return DEV_CERT_ORIGIN;
  }
  const swapped = swapAppLabel(hostname, "certification");
  if (swapped) {
    return `${protocol}//${swapped}`;
  }
  const port = currentPort();
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
}

/** Origin of the main pro-web product app. */
export function getProAppOrigin(): string {
  const hostname = currentHostname();
  const protocol = currentProtocol();

  if (isLoopbackHost(hostname)) {
    return `${protocol}//${hostname}:5173`;
  }
  if (isLocalSubdomainHost(hostname)) {
    // Prefer plain localhost for local returns so the auth cookie (host-only
    // on localhost) is visible after Google SSO.
    return `${protocol}//localhost:5173`;
  }
  if (isProdLionEnergyHost(hostname)) {
    return PROD_PRO_ORIGIN;
  }
  if (isDevLionEnergyHost(hostname)) {
    return DEV_PRO_ORIGIN;
  }
  const swapped = swapAppLabel(hostname, "pro");
  if (swapped) {
    return `${protocol}//${swapped}`;
  }
  const port = currentPort();
  return `${protocol}//${hostname}${port ? `:${port}` : ""}`;
}

export { isLocalDevHost };

/**
 * Guard against repeated cross-origin bounces while auth state is still
 * settling (AppShell effects re-fire on isAuthenticated/user changes). Without
 * this, Firefox hits "Too many calls to Location or History APIs" /
 * "The operation is insecure".
 */
let externalNavigationStarted = false;

/**
 * Hard-navigate once via location.replace (no history entry). Safe to call
 * repeatedly — subsequent calls are no-ops.
 */
export function hardNavigateOnce(url: string): boolean {
  if (typeof window === "undefined") return false;
  if (externalNavigationStarted) return false;
  // Already navigating to this URL (e.g. effect re-ran before unload).
  if (window.location.href === url) {
    externalNavigationStarted = true;
    return false;
  }
  externalNavigationStarted = true;
  window.location.replace(url);
  return true;
}

/**
 * Hard-navigate an unauthenticated visitor to account-web /login, preserving
 * the current URL as the post-auth return target.
 */
export function bounceToAccountLogin(returnTo: string = window.location.href): void {
  hardNavigateOnce(
    `${getAccountAppOrigin()}/login?redirect=${encodeURIComponent(returnTo)}`,
  );
}

/**
 * Preserve path + search when permanently redirecting an old pro-web auth URL
 * (e.g. /login?redirect=…) to the account app.
 */
export function bounceToAccountPath(pathname: string, search: string = ""): void {
  const qs = search.startsWith("?") || search === "" ? search : `?${search}`;
  hardNavigateOnce(`${getAccountAppOrigin()}${pathname}${qs}`);
}

/**
 * For TanStack beforeLoad: start an external navigation and halt the router
 * so it does not keep syncing history while the browser leaves the page.
 */
export function haltForExternalNavigation(): never {
  // Never-resolving promise stops beforeLoad without a client-side redirect loop.
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- intentional router halt
  throw new Promise<void>(() => {});
}
